import type { PlayerThemeId } from './playerTheme';
import { usesLogoTheme } from './playerTheme';

export const THEME_ICON_KEYS = [
  'sports-betting',
  'live-betting',
  'highlights',
  'live-casino',
  'casino-games',
  'deposit',
  'results',
  'search',
  'contact',
  'language',
  'other',
  'fullscreen',
  'login',
  'logout',
  'my-bets',
  'account-settings',
  'slip',
  'home',
  'my-favorites',
  'most-played-events',
  'last-minute-events',
  'today-events',
  'chevron-left',
  'chevron-right',
  'chevron-right-white',
  'chevron-right-blue',
  'chevron-down',
  'arrow-left',
  'arrow-right-white',
  'remove',
  'remove-black',
  'remove-gray',
  'check-lg',
  'check-lg-red',
  'check-lg-white',
  'checkmark',
  'checkmark-blue',
  'delete',
  'trash',
  'info',
  'info-circle',
  'info-full',
  'info-svg',
  'clock',
  'star',
  'star-inactive',
  'star-white',
  'person',
  'question',
  'success-checkmark',
  'validation-error',
  'live-on',
  'stats-on',
  'tv-on',
  'header-clock',
  'top-arrow',
  'desktop-my-bets',
  'desktop-deposit',
  'desktop-account-statement',
  'desktop-account-settings',
  'desktop-gaming-history',
  'panel-home',
  'printer',
  'record',
  'list-marker',
  'pw-info',
  'sport-ball',
  'gaming-history',
  'account-statement',
  'theme',
  // Footer trust strip; alt themes only, so they have no tottalo PNG below.
  'support',
  'secure-payments',
  'best-odds',
  'mobile-app',
  'fast-payout',
  'limits',
  'account-lock',
] as const;

export type ThemeIconKey = (typeof THEME_ICON_KEYS)[number];

const TOTTALO_ASSETS: Record<ThemeIconKey, string> = {
  'sports-betting': '/photos/icons/menu-icons/sports-betting.png',
  'live-betting': '/photos/icons/menu-icons/live-betting.png',
  highlights: '/photos/icons/menu-icons/highlights.png',
  'live-casino': '/photos/icons/menu-icons/live-casino.png',
  'casino-games': '/photos/icons/menu-icons/casino-games.png',
  deposit: '/photos/icons/menu-icons/deposit.png',
  results: '/photos/icons/menu-icons/results.png',
  search: '/photos/icons/menu-icons/search.png',
  contact: '/photos/icons/menu-icons/contact.png',
  language: '/photos/icons/menu-icons/language.png',
  other: '/photos/icons/menu-icons/other.png',
  fullscreen: '/photos/icons/menu-icons/fullscreen.svg',
  login: '/photos/icons/menu-icons/login.png',
  logout: '/photos/icons/menu-icons/logout.png',
  'my-bets': '/photos/icons/menu-icons/my-bets.png',
  'account-settings': '/photos/icons/menu-icons/account-settings.png',
  slip: '/photos/icons/menu-icons/slip.png',
  home: '/photos/icons/home.png',
  'my-favorites': '/photos/icons/menu-icons/my-favorites.png',
  'most-played-events': '/photos/icons/menu-icons/most-played-events.png',
  'last-minute-events': '/photos/icons/menu-icons/last-minute-events.png',
  'today-events': '/photos/icons/menu-icons/today-events.png',
  'chevron-left': '/photos/icons/chevron-left.svg',
  'chevron-right': '/photos/icons/chevron-right.svg',
  'chevron-right-white': '/photos/icons/chevron-right-white.svg',
  'chevron-right-blue': '/photos/icons/chevron-right-blue.svg',
  'chevron-down': '/photos/icons/chevron-down.png',
  'arrow-left': '/photos/icons/arrow-left.svg',
  'arrow-right-white': '/photos/icons/right-arrow-white.png',
  remove: '/photos/icons/remove-icon.svg',
  'remove-black': '/photos/icons/remove-icon-black.svg',
  'remove-gray': '/photos/icons/remove-icon-gray.svg',
  'check-lg': '/photos/icons/check-lg.svg',
  'check-lg-red': '/photos/icons/check-lg-red.svg',
  'check-lg-white': '/photos/icons/check-lg-white.svg',
  checkmark: '/photos/icons/checkmark.svg',
  'checkmark-blue': '/photos/icons/checkmark-blue.svg',
  delete: '/photos/icons/delete_icon.png',
  trash: '/photos/icons/trash.svg',
  info: '/photos/icons/info-circle.png',
  'info-circle': '/photos/icons/info-circle.svg',
  'info-full': '/photos/icons/info-full.png',
  'info-svg': '/photos/icons/info.svg',
  clock: '/photos/icons/clock.svg',
  star: '/photos/icons/star-blue.svg',
  'star-inactive': '/photos/icons/star-gray.svg',
  'star-white': '/photos/icons/star-white.svg',
  person: '/photos/icons/person.svg',
  question: '/photos/icons/questionmark.png',
  'success-checkmark': '/photos/icons/success-checkmark.png',
  'validation-error': '/photos/icons/validation-error.png',
  'live-on': '/photos/icons/live-on.png',
  'stats-on': '/photos/icons/stats-on.png',
  'tv-on': '/photos/icons/tv-on.png',
  'header-clock': '/photos/icons/header-clock.png',
  'top-arrow': '/photos/icons/top-arrow.png',
  'desktop-my-bets': '/photos/icons/desktop-my-bets.png',
  'desktop-deposit': '/photos/icons/desktop-deposit.png',
  'desktop-account-statement': '/photos/icons/desktop-account-statement.png',
  'desktop-account-settings': '/photos/icons/desktop-account-settings.png',
  'desktop-gaming-history': '/photos/icons/desktop-gaming-history.png',
  'panel-home': '/photos/icons/panel-home.png',
  printer: '/photos/icons/printer.png',
  record: '/photos/icons/record2.svg',
  'list-marker': '/photos/icons/list-marker.png',
  'pw-info': '/photos/icons/pw-info.png',
  'sport-ball': '/photos/icons/sports-icons/sports-icons.png',
  'gaming-history': '/photos/icons/desktop-gaming-history.png',
  'account-statement': '/photos/icons/desktop-account-statement.png',
  // No dedicated "theme" artwork in the tottalo PNG set — reuse the generic "other" glyph.
  theme: '/photos/icons/menu-icons/other.png',
  // The trust strip is an alt-theme feature; tottalo keeps its own footer
  // artwork and never renders it, so these fall back to the generic glyph.
  support: '/photos/icons/menu-icons/contact.png',
  'secure-payments': '/photos/icons/menu-icons/other.png',
  'best-odds': '/photos/icons/menu-icons/other.png',
  'mobile-app': '/photos/icons/menu-icons/other.png',
  'fast-payout': '/photos/icons/menu-icons/deposit.png',
  limits: '/photos/icons/menu-icons/other.png',
  'account-lock': '/photos/icons/menu-icons/other.png',
};

/** Map legacy img src paths → theme icon key. */
export const LEGACY_ICON_PATHS: Record<string, ThemeIconKey> = {
  '/photos/icons/menu-icons/sports-betting.png': 'sports-betting',
  '/photos/icons/menu-icons/live-betting.png': 'live-betting',
  '/photos/icons/menu-icons/highlights.png': 'highlights',
  '/photos/icons/menu-icons/live-casino.png': 'live-casino',
  '/photos/icons/menu-icons/casino-games.png': 'casino-games',
  '/photos/icons/menu-icons/deposit.png': 'deposit',
  '/photos/icons/menu-icons/results.png': 'results',
  '/photos/icons/menu-icons/search.png': 'search',
  '/photos/icons/menu-icons/contact.png': 'contact',
  '/photos/icons/menu-icons/language.png': 'language',
  '/photos/icons/menu-icons/other.png': 'other',
  '/photos/icons/menu-icons/fullscreen.svg': 'fullscreen',
  '/photos/icons/menu-icons/login.png': 'login',
  '/photos/icons/menu-icons/logout.png': 'logout',
  '/photos/icons/menu-icons/my-bets.png': 'my-bets',
  '/photos/icons/menu-icons/account-settings.png': 'account-settings',
  '/photos/icons/menu-icons/slip.png': 'slip',
  '/photos/icons/home.png': 'home',
  '/photos/icons/menu-icons/my-favorites.png': 'my-favorites',
  '/photos/icons/menu-icons/most-played-events.png': 'most-played-events',
  '/photos/icons/menu-icons/last-minute-events.png': 'last-minute-events',
  '/photos/icons/menu-icons/today-events.png': 'today-events',
  '/photos/icons/search.svg': 'search',
  'photos/icons/menu-icons/sports-betting.png': 'sports-betting',
  'photos/icons/record2.svg': 'record',
  '/photos/icons/chevron-left.svg': 'chevron-left',
  '/photos/icons/chevron-right.svg': 'chevron-right',
  '/photos/icons/chevron-right-white.svg': 'chevron-right-white',
  '/photos/icons/chevron-right-blue.svg': 'chevron-right-blue',
  '/photos/icons/chevron-down.png': 'chevron-down',
  '/photos/icons/arrow-left.svg': 'arrow-left',
  '/photos/icons/right-arrow-white.png': 'arrow-right-white',
  '/photos/icons/remove-icon.svg': 'remove',
  '/photos/icons/remove-icon-black.svg': 'remove-black',
  '/photos/icons/remove-icon-gray.svg': 'remove-gray',
  '/photos/icons/check-lg.svg': 'check-lg',
  '/photos/icons/check-lg-red.svg': 'check-lg-red',
  '/photos/icons/check-lg-white.svg': 'check-lg-white',
  '/photos/icons/checkmark.svg': 'checkmark',
  '/photos/icons/checkmark-blue.svg': 'checkmark-blue',
  '/photos/icons/delete_icon.png': 'delete',
  '/photos/icons/trash.svg': 'trash',
  '/photos/icons/info-circle.png': 'info',
  '/photos/icons/info-circle.svg': 'info-circle',
  '/photos/icons/info-full.png': 'info-full',
  '/photos/icons/info.svg': 'info-svg',
  '/photos/icons/clock.svg': 'clock',
  '/photos/icons/star-blue.svg': 'star',
  '/photos/icons/star-gray.svg': 'star-inactive',
  '/photos/icons/star-white.svg': 'star-white',
  '/photos/icons/person.svg': 'person',
  '/photos/icons/questionmark.png': 'question',
  '/photos/icons/success-checkmark.png': 'success-checkmark',
  '/photos/icons/validation-error.png': 'validation-error',
  '/photos/icons/live-on.png': 'live-on',
  '/photos/icons/stats-on.png': 'stats-on',
  '/photos/icons/tv-on.png': 'tv-on',
  '/photos/icons/header-clock.png': 'header-clock',
  '/photos/icons/top-arrow.png': 'top-arrow',
  '/photos/icons/desktop-my-bets.png': 'desktop-my-bets',
  '/photos/icons/desktop-deposit.png': 'desktop-deposit',
  '/photos/icons/desktop-account-statement.png': 'desktop-account-statement',
  '/photos/icons/desktop-account-settings.png': 'desktop-account-settings',
  '/photos/icons/desktop-gaming-history.png': 'desktop-gaming-history',
  '/photos/icons/panel-home.png': 'panel-home',
  '/photos/icons/printer.png': 'printer',
  '/photos/icons/pw-info.png': 'pw-info',
};

export function themeIconUsesPng(theme: PlayerThemeId): boolean {
  return usesLogoTheme(theme);
}

export function themeIconAssetUrl(key: ThemeIconKey): string {
  return TOTTALO_ASSETS[key];
}

/**
 * Alt-theme (non-tottalo) icon source: a 24x24 monochrome line glyph, applied as
 * a CSS mask and painted with `currentColor`, so one file serves every palette
 * and every size. This replaced a set of baked colour PNGs: those were emoji
 * artwork, which read as clip-art next to a dark, typographic UI.
 */
export function lineThemeIconAssetUrl(key: ThemeIconKey): string {
  return `/photos/icons/theme/line/${key}.svg`;
}

export function legacyPathToThemeIconKey(src: string): ThemeIconKey | null {
  const normalized = src.replace(/^\/public/, '');
  if (LEGACY_ICON_PATHS[normalized]) {
    return LEGACY_ICON_PATHS[normalized];
  }
  if (LEGACY_ICON_PATHS[src]) {
    return LEGACY_ICON_PATHS[src];
  }
  if (normalized.includes('/photos/icons/menu-icons/highlights.png')) {
    return 'highlights';
  }
  if (normalized.includes('/photos/icons/menu-icons/last-minute-events.png')) {
    return 'last-minute-events';
  }
  if (normalized.includes('/photos/icons/sports-icons/')) {
    return 'sport-ball';
  }
  return null;
}

export type MenuIconKey = ThemeIconKey;
export const MENU_ICON_KEYS = THEME_ICON_KEYS;
export const menuIconUsesPng = themeIconUsesPng;
export const menuIconPngUrl = themeIconAssetUrl;
